import Chart from 'chart.js/auto';
import { avg, sampleLabel } from './format.js';

/* Draws colored Low/Optimum/High (etc.) bands behind the bars, with the band name printed
   inside it, directly on the canvas. This used to lean on chartjs-plugin-annotation's box
   "label" sub-feature, which turned out not to render reliably — so instead this is a small
   self-contained Chart.js plugin (registered per-chart via the `plugins:` array, no separate
   library/version to line up with Chart.js). Chart config supplies its bands via
   `options.plugins.bandOverlay.bands`. */
export const bandOverlayPlugin = {
	id: 'bandOverlay',
	beforeDatasetsDraw(chart) {
		const bands = (chart.options.plugins.bandOverlay || {}).bands;
		if (!bands || !bands.length) return;
		const { ctx, chartArea, scales } = chart;
		const yScale = scales.y;
		ctx.save();
		bands.forEach(band => {
			const yTop = yScale.getPixelForValue(band.yMax);
			const yBottom = yScale.getPixelForValue(band.yMin);
			ctx.fillStyle = band.color;
			ctx.fillRect(chartArea.left, yTop, chartArea.right - chartArea.left, yBottom - yTop);
		});
		ctx.restore();
	},
	afterDatasetsDraw(chart) {
		const bands = (chart.options.plugins.bandOverlay || {}).bands;
		if (!bands || !bands.length) return;
		const { ctx, chartArea, scales } = chart;
		const yScale = scales.y;
		ctx.save();
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.font = "600 10px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
		bands.forEach(band => {
			if (!band.label) return;
			const yTop = yScale.getPixelForValue(band.yMax);
			const yBottom = yScale.getPixelForValue(band.yMin);
			if (yBottom - yTop < 12) return; // band too thin to hold legible text — skip rather than overlap
			const xCenter = chartArea.left + (chartArea.right - chartArea.left) / 2;
			ctx.fillStyle = band.labelColor || '#333';
			ctx.fillText(band.label, xCenter, (yTop + yBottom) / 2);
		});
		ctx.restore();
	}
};

export function sampleSeriesFor(samples, get) {
	const values = samples.map(get);
	const avgVal = avg(values);
	const labels = samples.map(sampleLabel).concat(['Average']);
	const data = values.concat([avgVal]);
	// Average bar rendered in the darker shade, same convention as the PDF's "A" column.
	const colors = data.map((v, i) => i === data.length - 1 ? '#123c22' : '#1e5b34');
	return { labels, data, colors };
}

export function axisRangeFor(data, range) {
	const numericData = data.filter(v => v != null);
	const dataMax = numericData.length ? Math.max(...numericData) : (range.max || 1);
	const axisMax = Math.max((range.max || 0) * 1.2, dataMax * 1.15, 1);
	return { axisMin: 0, axisMax };
}

// One colored horizontal band descriptor, optionally with a text label — drawn onto the
// canvas directly by bandOverlayPlugin (see above) rather than a third-party
// annotation library, so it doesn't depend on any plugin's box-label feature existing.
export function lowOptHighBands(range, axisMin, axisMax, bandLabels) {
	bandLabels = bandLabels || { low: 'Low', opt: 'Optimum', high: 'High' };
	const bands = [];
	if (range.min != null) {
		bands.push({ yMin: axisMin, yMax: range.min, color: 'rgba(247,230,163,0.65)', label: bandLabels.low, labelColor: '#7a5b00' });
	}
	bands.push({
		yMin: range.min != null ? range.min : axisMin,
		yMax: range.max != null ? range.max : axisMax,
		color: 'rgba(191,227,196,0.65)', label: bandLabels.opt, labelColor: '#1c5b2f'
	});
	if (range.max != null) {
		bands.push({ yMin: range.max, yMax: axisMax, color: 'rgba(240,198,160,0.65)', label: bandLabels.high, labelColor: '#8a4a1e' });
	}
	return bands;
}

// CEC → estimated soil texture. This is a fixed general reference classification
// (Andersons' page-4 interpretation guide), not a per-report optimum — so unlike the
// other charts, its bands never come from optimum_ranges and don't imply "good/bad."
export const CEC_TEXTURE_BANDS = [
	{ label: 'Clay / Clay Loam', min: 28, max: 40, color: 'rgba(63,138,82,0.75)' },
	{ label: 'Loam', min: 20, max: 28, color: 'rgba(116,184,131,0.65)' },
	{ label: 'Sandy / Silty Loam', min: 12, max: 20, color: 'rgba(169,214,178,0.6)' },
	{ label: 'Loamy Sand', min: 8, max: 12, color: 'rgba(207,232,212,0.6)' },
	{ label: 'Sand', min: 0, max: 8, color: 'rgba(234,245,236,0.6)' }
];

export function renderBarChart(ctx, { labels, data, colors, axisMin, axisMax, bands, unit }) {
	return new Chart(ctx, {
		type: 'bar',
		plugins: [bandOverlayPlugin],
		data: { labels, datasets: [{ data, backgroundColor: colors, borderRadius: 3, maxBarThickness: 26, order: 1 }] },
		options: {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: { display: false },
				tooltip: { callbacks: { label: c => c.formattedValue + (unit ? ' ' + unit : '') } },
				bandOverlay: { bands }
			},
			scales: {
				y: { min: axisMin, max: axisMax, grid: { color: '#eee' } },
				x: { grid: { display: false } }
			}
		}
	});
}
